// FORCE_SYNC_REV4: Agriculture balance is read from backend/MongoDB.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setInformacion } from '../../store/slices/usuarios';
import { getCookie } from '../../utils/getCookies';
import { URL_MAIN } from '../../rutas/rutas';
import './CityFarmAgricultureV2.css';

const API_BASE = `${URL_MAIN || ''}/api/cityfarm/v2/agriculture`;
const REQUEST_TIMEOUT_MS = 12000;
const CONFIG_POLL_MS = 5000;
const STATE_POLL_MS = 20000;

const TEXT = {
  es: {
    title: 'Distrito Agrícola V2',
    live: 'CONFIG EN VIVO',
    loading: 'Cargando agricultura...',
    unavailable: 'Agricultura V2 no está disponible ahora.',
    retry: 'REINTENTAR',
    close: 'CERRAR',
    open: 'ABRIR',
    balance: 'Saldo HUESO',
    globalBurned: 'HUESO retirado del juego',
    yourBurned: 'Tu HUESO usado aquí',
    harvests: 'Cosechas',
    plot: 'Parcela',
    level: 'Nivel',
    locked: 'BLOQUEADA',
    free: 'GRATIS',
    empty: 'LIBRE',
    growing: 'CRECIENDO',
    ready: 'LISTA',
    unlock: 'DESBLOQUEAR',
    burn: 'QUEMAR',
    upgrade: 'MEJORAR',
    maxLevel: 'NIVEL MÁXIMO',
    plant: 'SEMBRAR',
    harvest: 'COSECHAR',
    specialize: 'ESPECIALIZAR',
    permanent: 'La especialización es permanente.',
    requires: 'Requiere',
    inputs: 'Recursos',
    produces: 'Produce',
    duration: 'Duración',
    currentCrop: 'Cultivo actual',
    nextLevel: 'Próximo nivel',
    chooseCrop: 'Elige un cultivo',
    chooseSpec: 'Especialización permanente',
    noCrop: 'No hay cultivo activo.',
    confirmBurn: 'Esta operación descontará HUESO de tu saldo interno de City Farm.',
    processing: 'PROCESANDO...',
    configUpdated: 'Configuración actualizada',
    editHint: 'Precios y requisitos se leen desde MongoDB y se actualizan automáticamente.',
    needHarvests: 'cosechas totales',
    needPlots: 'parcelas desbloqueadas',
    weather: 'Clima visual',
    syncing: 'SINCRONIZANDO',
    apiLabel: 'API de Agricultura',
    getCloser: 'acércate para interactuar',
    invalidBackend: 'Backend Agriculture V2 sin respuesta válida',
    routeHint: 'Verifica que la ruta /api/cityfarm/v2/agriculture esté activa en el backend.',
    dbRevision: 'Rev. BD',
    actionFailed: 'No se pudo completar la acción agrícola.',
    configError: 'No se pudo cargar la configuración agrícola.',
    stateError: 'No se pudo cargar el estado agrícola.',
    remaining: 'restantes',
    interactHint: '[E] INTERACTUAR',
  },
  en: {
    title: 'Agriculture District V2',
    live: 'LIVE CONFIG',
    loading: 'Loading agriculture...',
    unavailable: 'Agriculture V2 is unavailable right now.',
    retry: 'RETRY',
    close: 'CLOSE',
    open: 'OPEN',
    balance: 'HUESO balance',
    globalBurned: 'HUESO removed from game',
    yourBurned: 'Your HUESO used here',
    harvests: 'Harvests',
    plot: 'Plot',
    level: 'Level',
    locked: 'LOCKED',
    free: 'FREE',
    empty: 'EMPTY',
    growing: 'GROWING',
    ready: 'READY',
    unlock: 'UNLOCK',
    burn: 'BURN',
    upgrade: 'UPGRADE',
    maxLevel: 'MAX LEVEL',
    plant: 'PLANT',
    harvest: 'HARVEST',
    specialize: 'SPECIALIZE',
    permanent: 'Specialization is permanent.',
    requires: 'Requires',
    inputs: 'Resources',
    produces: 'Produces',
    duration: 'Duration',
    currentCrop: 'Current crop',
    nextLevel: 'Next level',
    chooseCrop: 'Choose a crop',
    chooseSpec: 'Permanent specialization',
    noCrop: 'No active crop.',
    confirmBurn: 'This operation deducts HUESO from your internal City Farm balance.',
    processing: 'PROCESSING...',
    configUpdated: 'Configuration updated',
    editHint: 'Prices and requirements are read from MongoDB and refresh automatically.',
    needHarvests: 'total harvests',
    needPlots: 'unlocked plots',
    weather: 'Visual weather',
    syncing: 'SYNCING',
    apiLabel: 'Agriculture API',
    getCloser: 'get closer to interact',
    invalidBackend: 'Agriculture V2 backend is not returning valid data',
    routeHint: 'Check that /api/cityfarm/v2/agriculture is active on the backend.',
    dbRevision: 'DB rev.',
    actionFailed: 'The agriculture action could not be completed.',
    configError: 'The agriculture configuration could not be loaded.',
    stateError: 'The agriculture state could not be loaded.',
    remaining: 'remaining',
    interactHint: '[E] INTERACT',
  },
};



const RESOURCE_LABELS = {
  'corn seed': { es: 'Semilla de maíz', en: 'Corn seed' },
  'wheat seed': { es: 'Semilla de trigo', en: 'Wheat seed' },
  corn: { es: 'Maíz', en: 'Corn' },
  wheat: { es: 'Trigo', en: 'Wheat' },
  water: { es: 'Agua', en: 'Water' },
  energy: { es: 'Energía', en: 'Energy' },
};

const WEATHER_LABELS = {
  clear: { es: 'Despejado', en: 'Clear' },
  sunny: { es: 'Soleado', en: 'Sunny' },
  clouds: { es: 'Nublado', en: 'Cloudy' },
  cloudy: { es: 'Nublado', en: 'Cloudy' },
  rain: { es: 'Lluvia', en: 'Rain' },
  storm: { es: 'Tormenta', en: 'Storm' },
  fog: { es: 'Niebla', en: 'Fog' },
  snow: { es: 'Nieve', en: 'Snow' },
  wind: { es: 'Viento', en: 'Wind' },
};

const ERROR_TEXT = {
  AGRICULTURE_API_ROUTE_MISSING: {
    es: 'La ruta de Agriculture V2 no está activa en el backend.',
    en: 'The Agriculture V2 API route is not active on the backend.',
  },
  TIMEOUT: {
    es: 'El servidor de City Farm tardó demasiado en responder.',
    en: 'The City Farm server took too long to respond.',
  },
  INVALID_AGRICULTURE_CONFIG: {
    es: 'El backend devolvió una configuración de Agriculture V2 inválida.',
    en: 'The backend returned an invalid Agriculture V2 configuration.',
  },
  INVALID_AGRICULTURE_STATE: {
    es: 'El backend devolvió un estado de Agriculture V2 inválido.',
    en: 'The backend returned an invalid Agriculture V2 state.',
  },
  SESSION_EXPIRED: { es: 'Tu sesión expiró. Inicia sesión nuevamente.', en: 'Your session expired. Please sign in again.' },
  USER_NOT_FOUND: { es: 'No se encontró el usuario de City Farm.', en: 'The City Farm user was not found.' },
  RATE_LIMITED: { es: 'Demasiadas solicitudes. Espera un momento e inténtalo otra vez.', en: 'Too many requests. Wait a moment and try again.' },
  SSE_LIMITED: { es: 'Hay demasiadas conexiones en vivo. La actualización automática seguirá por consulta periódica.', en: 'There are too many live connections. Automatic updates will continue by polling.' },
  INVALID_PLOT: { es: 'La parcela seleccionada no es válida.', en: 'The selected plot is invalid.' },
  INVALID_INPUT: { es: 'Uno de los datos enviados no es válido.', en: 'One of the submitted values is invalid.' },
  IDEMPOTENCY_KEY_REQUIRED: { es: 'La operación no pudo validarse de forma segura. Recarga la página e inténtalo nuevamente.', en: 'The operation could not be validated safely. Reload the page and try again.' },
  INVALID_CLIENT: { es: 'La solicitud fue rechazada por seguridad.', en: 'The request was rejected for security reasons.' },
  JSON_REQUIRED: { es: 'La operación requiere datos JSON válidos.', en: 'The operation requires valid JSON data.' },
  INVALID_JSON_BODY: { es: 'Los datos enviados no son válidos.', en: 'The submitted data is invalid.' },
  BODY_TOO_LARGE: { es: 'La solicitud enviada es demasiado grande.', en: 'The submitted request is too large.' },
  SECURITY_STORE_UNAVAILABLE: { es: 'La protección de operaciones está temporalmente no disponible.', en: 'Operation protection is temporarily unavailable.' },
  IDEMPOTENCY_CONFLICT: { es: 'La operación segura ya fue utilizada con otra acción. Recarga la página antes de reintentar.', en: 'The secure operation key was already used for another action. Reload the page before retrying.' },
  ECONOMY_TRANSACTIONS_REQUIRED: { es: 'La operación económica está protegida: MongoDB debe soportar transacciones.', en: 'The economic operation is protected: MongoDB must support transactions.' },
  TRANSACTION_RETRY_FAILED: { es: 'No se pudo completar la operación de forma segura. Inténtalo nuevamente.', en: 'The operation could not be completed safely. Try again.' },
  FEATURE_DISABLED: { es: 'Agriculture V2 está desactivado actualmente.', en: 'Agriculture V2 is currently disabled.' },
  ALREADY_SPECIALIZED: { es: 'Esta parcela ya tiene una especialización permanente.', en: 'This plot already has a permanent specialization.' },
  ALREADY_UNLOCKED: { es: 'Esta parcela ya está desbloqueada.', en: 'This plot is already unlocked.' },
  CROP_DISABLED: { es: 'Ese cultivo está desactivado actualmente.', en: 'That crop is currently disabled.' },
  CROP_NOT_ALLOWED: { es: 'Ese cultivo no está permitido en esta parcela.', en: 'That crop is not allowed on this plot.' },
  CROP_NOT_READY: { es: 'El cultivo todavía no está listo para cosechar.', en: 'The crop is not ready to harvest yet.' },
  INVALID_SPECIALIZATION: { es: 'La especialización seleccionada no es válida.', en: 'The selected specialization is invalid.' },
  INVALID_YIELD: { es: 'La configuración de producción del cultivo no es válida.', en: 'The crop yield configuration is invalid.' },
  LEVEL_NOT_CONFIGURED: { es: 'El próximo nivel no está configurado.', en: 'The next level is not configured.' },
  MAX_LEVEL: { es: 'La parcela ya está en el nivel máximo.', en: 'The plot is already at the maximum level.' },
  MIN_PLOT_LEVEL: { es: 'La parcela todavía no tiene el nivel requerido.', en: 'The plot has not reached the required level yet.' },
  MIN_TOTAL_HARVESTS: { es: 'Aún no tienes suficientes cosechas totales.', en: 'You do not have enough total harvests yet.' },
  MIN_UNLOCKED_PLOTS: { es: 'Aún no tienes suficientes parcelas desbloqueadas.', en: 'You do not have enough unlocked plots yet.' },
  NOT_ENOUGH_HUESO: { es: 'No tienes suficiente HUESO para esta operación.', en: 'You do not have enough HUESO for this operation.' },
  NOT_ENOUGH_ITEM: { es: 'No tienes suficientes recursos para esta operación.', en: 'You do not have enough resources for this operation.' },
  NO_CROP: { es: 'No hay un cultivo activo en esta parcela.', en: 'There is no active crop on this plot.' },
  PLOT_BUSY: { es: 'La parcela ya tiene un cultivo activo.', en: 'The plot already has an active crop.' },
  PLOT_LOCKED: { es: 'La parcela está bloqueada.', en: 'The plot is locked.' },
  REQUIREMENT_NOT_MET: { es: 'Todavía no cumples los requisitos de esta operación.', en: 'You do not meet the requirements for this operation yet.' },
  AGRICULTURE_ERROR: { es: 'No se pudo completar la operación agrícola.', en: 'The agriculture operation could not be completed.' },
};

function localizedResourceName(itemName, language) {
  const key = String(itemName || '').trim().toLowerCase();
  return RESOURCE_LABELS[key]?.[language] || RESOURCE_LABELS[key]?.en || itemName;
}

function localizedWeatherName(weather, language) {
  const key = String(weather || 'clear').trim().toLowerCase();
  return WEATHER_LABELS[key]?.[language] || WEATHER_LABELS[key]?.en || weather || 'clear';
}

function localizedError(error, language) {
  if (!error) return '';
  const lang = language === 'en' ? 'en' : 'es';
  const code = String(error?.code || error?.payload?.error || '').trim();
  if (code && ERROR_TEXT[code]) return ERROR_TEXT[code][lang] || ERROR_TEXT[code].es;
  const status = Number(error?.status || 0);
  if (status === 401) return lang === 'es' ? 'Tu sesión expiró. Inicia sesión nuevamente.' : 'Your session expired. Please sign in again.';
  if (status === 429) return lang === 'es' ? 'Demasiadas solicitudes. Espera un momento e inténtalo otra vez.' : 'Too many requests. Wait a moment and try again.';
  if (status >= 500) return lang === 'es' ? 'El servidor no pudo completar la operación de forma segura. Inténtalo nuevamente.' : 'The server could not complete the operation safely. Try again.';
  if (typeof error === 'string') return error;
  return error?.message || (lang === 'es' ? 'Ocurrió un error en Agriculture V2.' : 'An Agriculture V2 error occurred.');
}

const fmt = (value, max = 3) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: max });

const formatTime = (seconds) => {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
};

const cropName = (crop, language) =>
  language === 'es' ? crop?.nameEs || crop?.nameEn : crop?.nameEn || crop?.nameEs;

const plotName = (plotCfg, language, id) =>
  (language === 'es' ? plotCfg?.nameEs || plotCfg?.nameEn : plotCfg?.nameEn || plotCfg?.nameEs) ||
  `${language === 'es' ? 'Parcela' : 'Plot'} ${id}`;

const specName = (spec, language, key) =>
  (language === 'es' ? spec?.nameEs || spec?.nameEn : spec?.nameEn || spec?.nameEs) || key;

function authHeaders() {
  const token = getCookie('token');
  return token ? { token } : {};
}

const MUTATION_STORAGE_KEY = 'cityfarm:agriculture-v2:pending-operation';
const MUTATION_TTL_MS = 5 * 60 * 1000;

function stablePayload(value) {
  if (Array.isArray(value)) return value.map(stablePayload);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value)
    .sort()
    .reduce((out, key) => {
      out[key] = stablePayload(value[key]);
      return out;
    }, {});
}

function mutationSignature(action, payload) {
  return `${String(action)}:${JSON.stringify(stablePayload(payload || {}))}`;
}

function newIdempotencyKey() {
  try {
    if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
    if (window?.crypto?.getRandomValues) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (_) {}
  return `cfagri-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}

function loadPendingMutationMap() {
  try {
    const raw = window.sessionStorage.getItem(MUTATION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const clean = {};
    Object.entries(parsed || {}).forEach(([signature, entry]) => {
      if (
        entry?.key &&
        now - Number(entry?.createdAt || 0) <= MUTATION_TTL_MS
      ) {
        clean[signature] = entry;
      }
    });
    window.sessionStorage.setItem(MUTATION_STORAGE_KEY, JSON.stringify(clean));
    return clean;
  } catch (_) {
    return {};
  }
}

function loadPendingMutation(signature) {
  return loadPendingMutationMap()?.[signature] || null;
}

function savePendingMutation(signature, key) {
  try {
    const map = loadPendingMutationMap();
    map[signature] = { key, createdAt: Date.now() };
    window.sessionStorage.setItem(MUTATION_STORAGE_KEY, JSON.stringify(map));
  } catch (_) {}
}

function clearPendingMutation(signature) {
  try {
    if (!signature) {
      window.sessionStorage.removeItem(MUTATION_STORAGE_KEY);
      return;
    }
    const map = loadPendingMutationMap();
    delete map[signature];
    window.sessionStorage.setItem(MUTATION_STORAGE_KEY, JSON.stringify(map));
  } catch (_) {}
}

function isUncertainNetworkFailure(error) {
  return (
    error?.code === 'TIMEOUT' ||
    Number(error?.status || 0) >= 500 ||
    error?.name === 'TypeError'
  );
}

function isSeedItem(itemName) {
  return /\bseed\b/i.test(String(itemName || ''));
}

async function api(path, options = {}) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...(options.headers || {}),
      },
      signal: controller.signal,
    });
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    if (!contentType.includes('application/json')) {
      const routeError = new Error('Agriculture V2 API route is not active on the backend.');
      routeError.code = 'AGRICULTURE_API_ROUTE_MISSING';
      routeError.status = response.status;
      throw routeError;
    }
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body?.ok === false) {
      const error = new Error(body?.message || body?.error || `HTTP ${response.status}`);
      error.code = body?.error || `HTTP_${response.status}`;
      error.status = response.status;
      error.payload = body;
      throw error;
    }
    return body;
  } catch (error) {
    if (error?.name === 'AbortError') {
      const timeout = new Error('City Farm server took too long to respond.');
      timeout.code = 'TIMEOUT';
      throw timeout;
    }
    throw error;
  } finally {
    window.clearTimeout(timer);
  }
}

function inventoryAmount(informacion, itemName) {
  const target = String(itemName || '').trim().toLowerCase();
  const item = (informacion?.inventario || []).find(
    (entry) => String(entry?.item || '').trim().toLowerCase() === target
  );
  return Number(item?.cantidad || 0);
}

function resourceLine(inputs, informacion) {
  return Object.entries(inputs || {}).map(([item, amount]) => ({
    item,
    amount: Number(amount || 0),
    have: inventoryAmount(informacion, item),
  }));
}


function stopPortalPointer(event) {
  // React portals siguen propagando eventos por el árbol React. Sin este stop,
  // el viewport principal recibe pointerdown y captura el puntero, haciendo caminar
  // al avatar y evitando que CLOSE/X reciban el click completo.
  event.stopPropagation();
}

function backdropClick(event, onClose) {
  event.stopPropagation();
  if (event.target === event.currentTarget) onClose();
}

function effectiveRecipePreview(config, plot, cropCfg) {
  const levelCfg = config?.levels?.[String(plot?.level || 1)] || config?.levels?.['1'] || {};
  const spec = plot?.specialization ? config?.specializations?.[plot.specialization] : null;
  const plotCfg = config?.plots?.[String(plot?.plotId || '')] || {};
  const plotDurationMinutes = Number(plotCfg?.productionDurationMinutes || 0);
  const cropDurationMinutes = Number(cropCfg?.durationMinutes || 0);
  const baseDurationMinutes = plotDurationMinutes > 0 ? plotDurationMinutes : cropDurationMinutes;
  const durationMultiplier = Number(levelCfg?.durationMultiplier || 1) * Number(spec?.durationMultiplier || 1);
  const yieldMultiplier = Number(levelCfg?.yieldMultiplier || 1) * Number(spec?.yieldMultiplier || 1);
  const inputMultiplier = Number(levelCfg?.inputMultiplier || 1) * Number(spec?.inputMultiplier || 1);
  const inputs = {};
  Object.entries(cropCfg?.inputs || {}).forEach(([item, amount]) => {
    const baseAmount = Math.max(0, Number(amount || 0));
    inputs[item] = isSeedItem(item)
      ? Math.ceil(baseAmount)
      : Math.ceil(baseAmount * inputMultiplier * 1000) / 1000;
  });
  return {
    durationSeconds: Math.max(60, Math.round(baseDurationMinutes * 60 * durationMultiplier)),
    yieldAmount: Math.floor(Math.max(0, Number(cropCfg?.yield?.amount || 0) * yieldMultiplier) * 1000) / 1000,
    inputs,
  };
}

function PlotOverlay({ field, plotId, plot, config, language, weather, disabled, isNearby, serviceReady, onOpen }) {
  const t = TEXT[language] || TEXT.es;
  const crop = plot?.crop;
  const locked = !plot?.unlocked;
  const ready = Boolean(crop?.ready);
  const growing = Boolean(crop && !ready);
  const status = !serviceReady ? 'syncing' : locked ? 'locked' : ready ? 'ready' : growing ? 'growing' : 'empty';
  const progress = Math.max(0, Math.min(1, Number(crop?.progress || 0)));
  const cropKey = String(crop?.cropKey || '').toLowerCase();
  const growthStage = ready ? 5 : !crop ? 0 : Math.min(4, Math.max(1, Math.ceil(progress * 4)));
  const spec = plot?.specialization ? config?.specializations?.[plot.specialization] : null;

  return (
    <button
      type="button"
      className={`cfagri-plot cfagri-${status} weather-${weather || 'clear'} ${disabled ? 'is-disabled' : ''} ${isNearby ? 'is-nearby' : 'is-out-of-range'}`}
      style={{
        left: field.x,
        top: field.y,
        width: field.width,
        height: field.height,
        zIndex: 650 + Number(field.y || 0),
        '--cfagri-progress': `${Math.round(progress * 100)}%`,
      }}
      onClick={(event) => {
        if (disabled || !isNearby) return;
        event.preventDefault();
        event.stopPropagation();
        onOpen(plotId);
      }}
      disabled={disabled}
      tabIndex={isNearby && !disabled ? 0 : -1}
      aria-disabled={disabled || !isNearby}
      aria-label={`${t.plot} ${plotId}`}
      title={isNearby ? `${t.plot} ${plotId} · ${t.open || t.plant}` : `${t.plot} ${plotId} · ${t.getCloser}`}
    >
      <span className="cfagri-plot-glow" />

      {crop && !locked && (
        <span
          className={`cfagri-growth-bed crop-${cropKey || 'generic'} stage-${growthStage} ${ready ? 'is-ready' : ''}`}
          aria-hidden="true"
        >
          {Array.from({ length: 12 }).map((_, plantIndex) => (
            <i key={plantIndex} className={`plant plant-${plantIndex + 1}`}>
              <b />
              <em />
              <strong />
            </i>
          ))}
        </span>
      )}

      {serviceReady && (
        <span className={`cfagri-plot-marker status-${status}`} aria-hidden="true">
          {locked ? '🔒' : ready ? '✓' : growing ? (crop?.emoji || '🌱') : '＋'}
        </span>
      )}

      {!serviceReady && <span className="cfagri-plot-marker status-syncing" aria-hidden="true">⏳</span>}
      {ready && <span className="cfagri-ready-spark">✦</span>}
      {plot?.specialization && <span className="cfagri-specialized">{spec?.emoji || '✨'}</span>}
    </button>
  );
}

function PlotContextHud({ plotId, plot, config, language, serviceReady }) {
  if (!plotId || !plot) return null;
  const t = TEXT[language] || TEXT.es;
  const cfg = config?.plots?.[String(plotId)] || {};
  const crop = plot?.crop;
  const locked = !plot?.unlocked;
  const ready = Boolean(crop?.ready);
  const progress = Math.max(0, Math.min(1, Number(crop?.progress || 0)));
  const price = Number(cfg?.unlock?.burnHueso || 0);
  const title = plotName(cfg, language, plotId);
  const cropName = crop ? (language === 'es' ? crop?.nameEs || crop?.nameEn : crop?.nameEn || crop?.nameEs) : '';

  let primary = `⏳ ${t.syncing}`;
  let secondary = t.apiLabel;
  if (serviceReady) {
    if (locked) {
      primary = `🔒 ${t.locked}`;
      secondary = cfg?.unlock?.free ? t.free : `🔥 ${fmt(price)} HUESO`;
    } else if (ready) {
      primary = `${crop?.emoji || '🌱'} ${cropName || t.currentCrop}`;
      secondary = `✨ ${t.ready} · ${t.harvest}`;
    } else if (crop) {
      primary = `${crop?.emoji || '🌱'} ${cropName || t.currentCrop}`;
      secondary = `${t.growing} · ${Math.round(progress * 100)}% · ${formatTime(crop?.remainingSeconds)} ${t.remaining}`;
    } else {
      primary = `＋ ${t.empty}`;
      secondary = t.plant;
    }
  }

  return (
    <div className="cfagri-context-hud" role="status" aria-live="polite">
      <div className="cfagri-context-title">
        <strong>{title}</strong>
        <span>LV {plot?.level || 1}</span>
      </div>
      <div className="cfagri-context-state">
        <b>{primary}</b>
        <small>{secondary}</small>
      </div>
      <div className="cfagri-context-action">{t.interactHint}</div>
    </div>
  );
}

function AgriculturePanel({ open, onClose, plotId, state, config, language, weather, informacion, busy, onAction, error, onRetry }) {
  if (!open || !plotId) return null;
  const t = TEXT[language] || TEXT.es;

  if (!state || !config || !state?.plots?.[String(plotId)]) {
    return createPortal(
      <div
        className="cfagri-modal-backdrop"
        onPointerDown={stopPortalPointer}
        onPointerMove={stopPortalPointer}
        onPointerUp={stopPortalPointer}
        onPointerCancel={stopPortalPointer}
        onWheel={stopPortalPointer}
        onClick={(event) => backdropClick(event, onClose)}
        role="presentation"
      >
        <section className="cfagri-panel cfagri-unavailable-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <header className="cfagri-panel-header">
            <div>
              <span className="cfagri-live-dot">● API</span>
              <h2>🌱 {t.plot} {plotId}</h2>
              <p>{t.unavailable}</p>
            </div>
            <button type="button" onClick={(event) => { event.stopPropagation(); onClose(); }}>×</button>
          </header>
          <div className="cfagri-panel-grid">
            <div className="cfagri-card">
              <h3>⚠️ {t.invalidBackend}</h3>
              <p>{localizedError(error, language) || t.routeHint}</p>
              <button className="cfagri-main-action" type="button" onClick={onRetry}>{t.retry}</button>
            </div>
          </div>
          <footer className="cfagri-panel-footer">
            <span>API: {API_BASE}</span>
            <button type="button" onClick={(event) => { event.stopPropagation(); onClose(); }}>{t.close}</button>
          </footer>
        </section>
      </div>,
      document.body
    );
  }
  const plot = state?.plots?.[String(plotId)] || null;
  const plotCfg = config?.plots?.[String(plotId)] || {};
  const crop = plot?.crop;
  const locked = !plot?.unlocked;
  const nextLevelCfg = plot?.nextLevelConfig;
  const specializations = Object.entries(config?.specializations || {}).filter(([, spec]) => spec?.enabled !== false);
  const allowedCrops = (plotCfg?.allowedCrops || [])
    .map((key) => [key, config?.crops?.[key]])
    .filter(([, value]) => value?.enabled !== false && value);

  const unlockedCount = Object.values(state?.plots || {}).filter((entry) => entry?.unlocked).length;
  const unlock = plotCfg?.unlock || {};
  const unlockRequirements = [];
  if (Number(unlock.minUnlockedPlots || 0) > 0) {
    unlockRequirements.push(`${unlockedCount}/${unlock.minUnlockedPlots} ${t.needPlots}`);
  }
  if (Number(unlock.minTotalHarvests || 0) > 0) {
    unlockRequirements.push(`${state?.totals?.totalHarvests || 0}/${unlock.minTotalHarvests} ${t.needHarvests}`);
  }

  return createPortal(
    <div
        className="cfagri-modal-backdrop"
        onPointerDown={stopPortalPointer}
        onPointerMove={stopPortalPointer}
        onPointerUp={stopPortalPointer}
        onPointerCancel={stopPortalPointer}
        onWheel={stopPortalPointer}
        onClick={(event) => backdropClick(event, onClose)}
        role="presentation"
      >
      <section
        className={`cfagri-panel weather-${weather || 'clear'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${t.plot} ${plotId}`}
      >
        <header className="cfagri-panel-header">
          <div>
            <span className="cfagri-live-dot">● {t.live}</span>
            <h2>🌱 {plotName(plotCfg, language, plotId)}</h2>
            <p>{t.editHint}</p>
          </div>
          <button type="button" onClick={(event) => { event.stopPropagation(); onClose(); }}>×</button>
        </header>

        <div className="cfagri-stats-row">
          <div><small>{t.balance}</small><strong>🔥 {fmt(state?.balanceHueso ?? informacion?.hueso)} </strong></div>
          <div><small>{t.yourBurned}</small><strong>{fmt(state?.totals?.totalHuesoBurned)} HUESO</strong></div>
          <div><small>{t.globalBurned}</small><strong>{fmt(state?.totals?.globalHuesoBurned)} HUESO</strong></div>
          <div><small>{t.harvests}</small><strong>{state?.totals?.totalHarvests || 0}</strong></div>
        </div>

        <div className="cfagri-panel-grid">
          <div className="cfagri-card cfagri-primary-card">
            <div className="cfagri-card-title">
              <span>{t.plot} {plotId}</span>
              <b>LV {plot?.level || 1}</b>
            </div>

            {locked ? (
              <div className="cfagri-lock-box">
                <div className="cfagri-big-icon">🔒</div>
                <h3>{t.locked}</h3>
                {!!unlockRequirements.length && (
                  <div className="cfagri-requirements">
                    <strong>{t.requires}:</strong>
                    {unlockRequirements.map((line) => <span key={line}>{line}</span>)}
                  </div>
                )}
                <p>{t.confirmBurn}</p>
                <button
                  className="cfagri-main-action"
                  disabled={busy}
                  onClick={() => onAction('unlock', { plotId })}
                >
                  {busy ? t.processing : unlock.free ? `${t.unlock} · ${t.free}` : `🔥 ${t.unlock} · ${fmt(unlock.burnHueso)} HUESO`}
                </button>
              </div>
            ) : (
              <>
                <div className={`cfagri-crop-status ${crop?.ready ? 'is-ready' : crop ? 'is-growing' : 'is-empty'}`}>
                  <span>{crop?.emoji || '🌱'}</span>
                  <div>
                    <small>{t.currentCrop}</small>
                    <strong>{crop ? (language === 'es' ? crop.nameEs : crop.nameEn) : t.noCrop}</strong>
                    {crop && <em>{crop.ready ? t.ready : `${Math.round(Number(crop.progress || 0) * 100)}% · ${formatTime(crop.remainingSeconds)}`}</em>}
                  </div>
                </div>

                {crop?.ready && (
                  <button className="cfagri-main-action is-ready" disabled={busy} onClick={() => onAction('harvest', { plotId })}>
                    {busy ? t.processing : `🌾 ${t.harvest}`}
                  </button>
                )}

                {!crop && (
                  <div className="cfagri-choice-block">
                    <h3>{t.chooseCrop}</h3>
                    <div className="cfagri-choice-grid">
                      {allowedCrops.map(([key, cropCfg]) => {
                        const preview = effectiveRecipePreview(config, plot, cropCfg);
                        const inputs = resourceLine(preview.inputs, informacion);
                        return (
                          <button key={key} type="button" disabled={busy} onClick={() => onAction('plant', { plotId, crop: key })}>
                            <b>{cropCfg?.emoji || '🌱'} {cropName(cropCfg, language)}</b>
                            <small>{t.duration}: {formatTime(preview.durationSeconds)}</small>
                            <small>{t.produces}: {fmt(preview.yieldAmount)} {localizedResourceName(cropCfg?.yield?.item, language)}</small>
                            <span className="cfagri-resource-mini">
                              {inputs.map(({ item, amount, have }) => (
                                <em className={have >= amount ? 'ok' : 'low'} key={item}>{localizedResourceName(item, language)}: {fmt(have)}/{fmt(amount)}</em>
                              ))}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {!locked && (
            <div className="cfagri-card">
              <h3>⬆️ {t.nextLevel}</h3>
              {nextLevelCfg ? (
                <>
                  <div className="cfagri-level-effects">
                    <span>⏱ ×{fmt(nextLevelCfg.durationMultiplier)}</span>
                    <span>🌾 ×{fmt(nextLevelCfg.yieldMultiplier)}</span>
                    <span>💧 ×{fmt(nextLevelCfg.inputMultiplier)}</span>
                  </div>
                  {Number(nextLevelCfg.minTotalHarvests || 0) > 0 && (
                    <p>{t.requires}: {state?.totals?.totalHarvests || 0}/{nextLevelCfg.minTotalHarvests} {t.needHarvests}</p>
                  )}
                  <button className="cfagri-secondary-action" disabled={busy} onClick={() => onAction('upgrade', { plotId })}>
                    {busy ? t.processing : `🔥 ${t.upgrade} · ${fmt(nextLevelCfg.burnHueso)} HUESO`}
                  </button>
                </>
              ) : (
                <div className="cfagri-max-level">🏆 {t.maxLevel}</div>
              )}

              {!plot?.specialization && Number(plot?.level || 1) >= 5 && (
                <div className="cfagri-choice-block cfagri-spec-block">
                  <h3>✨ {t.chooseSpec}</h3>
                  <p>{t.permanent}</p>
                  <div className="cfagri-spec-grid">
                    {specializations.map(([key, spec]) => (
                      <button key={key} disabled={busy || Number(plot.level) < Number(spec.requiredLevel || 5)} onClick={() => onAction('specialize', { plotId, specialization: key })}>
                        <b>{spec.emoji || '✨'} {specName(spec, language, key)}</b>
                        <small>🔥 {fmt(spec.burnHueso)} HUESO · LV {spec.requiredLevel}</small>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {plot?.specialization && (
                <div className="cfagri-active-spec">
                  <span>{config?.specializations?.[plot.specialization]?.emoji || '✨'}</span>
                  <div><small>{t.specialize}</small><strong>{specName(config?.specializations?.[plot.specialization], language, plot.specialization)}</strong></div>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="cfagri-panel-footer">
          <span>{t.dbRevision} {config?.revision || 1}</span>
          <span>{t.weather}: {localizedWeatherName(weather, language)}</span>
          <button type="button" onClick={(event) => { event.stopPropagation(); onClose(); }}>{t.close}</button>
        </footer>
      </section>
    </div>,
    document.body
  );
}

export default function CityFarmAgricultureV2({
  plots = [],
  language = 'es',
  weather = 'clear',
  layoutEditMode = false,
  nearbyPlotId = null,
}) {
  const t = TEXT[language] || TEXT.es;
  const dispatch = useDispatch();
  const { informacion } = useSelector((state) => state.userSlice);
  const informacionRef = useRef(informacion);
  const [config, setConfig] = useState(null);
  const [state, setState] = useState(null);
  const [selectedPlotId, setSelectedPlotId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => { informacionRef.current = informacion; }, [informacion]);

  // Solo abre una parcela si ESA parcela es realmente la que está dentro del radio de interacción.
  // Esto evita abrir agricultura haciendo clic desde lejos o despachando el evento fuera de proximidad.
  useEffect(() => {
    const handleWorldOpen = (event) => {
      const plotId = Number(event?.detail?.plotId);
      if (!Number.isInteger(plotId) || plotId < 1 || plotId > 6) return;
      if (Number(nearbyPlotId) !== plotId) return;
      setSelectedPlotId(plotId);
    };
    window.addEventListener('cityfarm:agriculture:open', handleWorldOpen);
    return () => window.removeEventListener('cityfarm:agriculture:open', handleWorldOpen);
  }, [nearbyPlotId]);

  // Cierre de emergencia/teclado. El panel SIEMPRE debe poder cerrarse, incluso si una petición está en curso.
  useEffect(() => {
    if (!selectedPlotId) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedPlotId(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPlotId]);

  useEffect(() => {
    if (layoutEditMode && selectedPlotId) setSelectedPlotId(null);
  }, [layoutEditMode, selectedPlotId]);


  // Bloquea el mundo principal mientras el panel Agriculture esté abierto.
  useEffect(() => {
    const open = Boolean(selectedPlotId);
    document.body?.classList?.toggle('cfagri-modal-open', open);
    window.dispatchEvent(new CustomEvent('cityfarm:agriculture:modal-state', { detail: { open } }));
    return () => {
      document.body?.classList?.remove('cfagri-modal-open');
      window.dispatchEvent(new CustomEvent('cityfarm:agriculture:modal-state', { detail: { open: false } }));
    };
  }, [selectedPlotId]);

  const closeAgriculturePanel = useCallback(() => {
    setSelectedPlotId(null);
  }, []);

  const mergeUser = useCallback((partial) => {
    if (!partial) return;
    const merged = { ...(informacionRef.current || {}), ...partial };
    if (partial.inventario) merged.inventario = partial.inventario;
    informacionRef.current = merged;
    dispatch(setInformacion(merged));
  }, [dispatch]);

  const loadConfig = useCallback(async (silent = false) => {
    try {
      const data = await api('/config');
      if (!data?.config?.plots || !data?.config?.levels || !data?.config?.crops) {
        const invalid = new Error('Agriculture V2 config response is invalid.');
        invalid.code = 'INVALID_AGRICULTURE_CONFIG';
        throw invalid;
      }
      setConfig(data.config);
      return { ok: true };
    } catch (err) {
      setConfig(null);
      if (!silent) setError(err || new Error(t.configError));
      return { ok: false, error: err };
    }
  }, [t.configError]);

  const loadState = useCallback(async (silent = false) => {
    try {
      const data = await api('/state');
      if (!data?.plots || typeof data.plots !== 'object') {
        const invalid = new Error('Agriculture V2 state response is invalid.');
        invalid.code = 'INVALID_AGRICULTURE_STATE';
        throw invalid;
      }
      setState(data);
      return { ok: true };
    } catch (err) {
      setState(null);
      if (!silent) setError(err || new Error(t.stateError));
      return { ok: false, error: err };
    }
  }, [t.stateError]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [cfgResult, stateResult] = await Promise.all([loadConfig(true), loadState(true)]);
    if (cfgResult.ok && stateResult.ok) {
      setError(null);
    } else {
      const firstError = cfgResult.error || stateResult.error;
      setError(firstError || new Error(t.unavailable));
    }
    setLoading(false);
  }, [loadConfig, loadState, t.unavailable]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const timer = window.setInterval(() => loadConfig(true), CONFIG_POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadConfig]);

  useEffect(() => {
    const timer = window.setInterval(() => loadState(true), STATE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadState]);

  useEffect(() => {
    // Lets progress bars/countdowns move smoothly without extra backend traffic.
    const timer = window.setInterval(() => {
      setTick((value) => value + 1);
      setState((current) => {
        if (!current?.plots) return current;
        const now = Date.now();
        const nextPlots = { ...current.plots };
        let changed = false;
        Object.keys(nextPlots).forEach((key) => {
          const plot = nextPlots[key];
          if (!plot?.crop?.readyAt) return;
          const readyAt = new Date(plot.crop.readyAt).getTime();
          const plantedAt = new Date(plot.crop.plantedAt).getTime();
          const durationMs = Math.max(1000, Number(plot.crop.durationSeconds || 1) * 1000);
          const remainingSeconds = Math.max(0, Math.ceil((readyAt - now) / 1000));
          const progress = Math.max(0, Math.min(1, (now - plantedAt) / durationMs));
          const ready = remainingSeconds <= 0;
          nextPlots[key] = { ...plot, crop: { ...plot.crop, remainingSeconds, progress, ready } };
          changed = true;
        });
        return changed ? { ...current, plots: nextPlots } : current;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let source = null;
    try {
      source = new EventSource(`${API_BASE}/config/stream`, { withCredentials: true });
      source.addEventListener('config', () => loadConfig(true));
      source.onerror = () => {
        // Polling continues even if SSE cannot authenticate in local development.
      };
    } catch (_) {}
    return () => { try { source?.close(); } catch (_) {} };
  }, [loadConfig]);

  const doAction = useCallback(async (action, payload) => {
    if (busy) return;

    const safePayload = payload || {};
    const signature = mutationSignature(action, safePayload);
    const pending = loadPendingMutation(signature);
    const idempotencyKey = pending?.key || newIdempotencyKey();

    if (!pending) savePendingMutation(signature, idempotencyKey);

    setBusy(true);
    setError(null);
    try {
      const data = await api(`/${action}`, {
        method: 'POST',
        body: JSON.stringify(safePayload),
        headers: {
          'X-CityFarm-Client': 'cityfarm-agriculture-v2',
          'X-Idempotency-Key': idempotencyKey,
        },
      });

      clearPendingMutation(signature);
      if (data?.state) setState(data.state);
      if (data?.user) mergeUser(data.user);
      await loadConfig(true);
    } catch (err) {
      if (!isUncertainNetworkFailure(err)) {
        clearPendingMutation(signature);
      }
      setError(err || new Error(t.actionFailed));
    } finally {
      setBusy(false);
    }
  }, [busy, loadConfig, mergeUser, t.actionFailed]);

  const fields = useMemo(() => {
    const source = Array.isArray(plots) ? plots : [];
    return source
      .filter((field) => /^simple-field-[1-6]$/.test(String(field?.id || '')))
      .map((field) => ({ ...field, plotId: Number(String(field.id).split('-').pop()) }))
      .sort((a, b) => a.plotId - b.plotId);
  }, [plots]);

  if (loading && !state && !config) {
    return <div className="cfagri-world-status">🌱 {t.loading}</div>;
  }

  if (config?.enabled === false) return null;

  return (
    <>
      <div className={`cfagri-world-layer ${layoutEditMode ? 'is-editing' : ''}`} aria-label={t.title}>
        {fields.map((field) => (
          <PlotOverlay
            key={field.id}
            field={field}
            plotId={field.plotId}
            plot={state?.plots?.[String(field.plotId)]}
            config={config}
            language={language}
            weather={weather}
            isNearby={Number(nearbyPlotId) === field.plotId}
            serviceReady={Boolean(state && config)}
            disabled={layoutEditMode || field.visible === false}
            onOpen={(plotId) => {
              if (Number(nearbyPlotId) === Number(plotId)) setSelectedPlotId(Number(plotId));
            }}
          />
        ))}

      </div>

      {!layoutEditMode && Number(nearbyPlotId) > 0 && createPortal(
        <PlotContextHud
          plotId={Number(nearbyPlotId)}
          plot={state?.plots?.[String(nearbyPlotId)]}
          config={config || {}}
          language={language}
          serviceReady={Boolean(state && config)}
        />,
        document.body
      )}

      {error && createPortal(
        <div
          className="cfagri-error-toast"
          role="alert"
          onPointerDown={stopPortalPointer}
          onPointerMove={stopPortalPointer}
          onPointerUp={stopPortalPointer}
          onPointerCancel={stopPortalPointer}
          onClick={stopPortalPointer}
        >
          <span>⚠️ {localizedError(error, language)}</span>
          <button type="button" onClick={loadAll}>{t.retry}</button>
        </div>,
        document.body
      )}

      <AgriculturePanel
        open={Boolean(selectedPlotId)}
        onClose={closeAgriculturePanel}
        plotId={selectedPlotId}
        state={state}
        config={config || {}}
        language={language}
        weather={weather}
        informacion={informacion}
        busy={busy}
        onAction={doAction}
        error={error}
        onRetry={loadAll}
      />
    </>
  );
}
